import { useTranslations } from '@/i18n/use-translations';
import { Box, Typography, TextField, MenuItem, Stack, Switch } from '@mui/material';
import { Controller, type UseFormReturn, useWatch } from 'react-hook-form';
import { useEventStore } from '@/state/stores/use-event-store';
import { useEffect, useMemo } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import { EVENT_TYPE } from '@/types/dtos/events/event-type.enum';
import { updateEventSchemaFactory } from '@/schemas/event/update-event';
import type { z } from 'zod';
import { MAX_EVENT_DAYS } from '@/constants';
import { timeToMinuteOffset } from '@/components/events/helpers/time-to-minute-offset.helper';
import { useFeatureAccess } from '@/hooks/use-feature-access';

interface DayConfig {
  startOffsetMinutes: number;
  openOffsetMinutes: number;
  endOffsetMinutes: number;
}
const START_TIME = '09:00';
const OPEN_TIME = '08:30';
const END_TIME = '18:00';

export default function GeneralForm({
  form,
}: {
  form: UseFormReturn<z.infer<ReturnType<typeof updateEventSchemaFactory>>>;
}) {
  const event = useEventStore((s) => s.event);
  const eventFlows = useEventStore((s) => s.eventFlows);
  const t = useTranslations();
  const { hasAccess: hasMultiFlowAccess } = useFeatureAccess('multi-flow');

  const durationDays = useWatch({ control: form.control, name: 'durationDays' });
  const multiFlow = useWatch({ control: form.control, name: 'multiFlow' });

  // Show multi-flow components if user has feature flag OR event is already multi-flow
  const showMultiFlowComponents = hasMultiFlowAccess || event?.multiFlow === true;

  // we need to check if there is already slot in some day and force selection to at least that many days
  const minimumDurationDays = useMemo(() => {
    if (!event?.days || !event.slots) return;
    const dayIdsUsed = event.slots.map((slot) => slot.day.id);
    const usedDaysIndexes = dayIdsUsed.map((dayId) => event.days.findIndex((day) => day.id === dayId) + 1);
    return Math.max(...usedDaysIndexes);
  }, [event]);

  useEffect(() => {
    if (durationDays > 1 && !multiFlow) {
      form.setValue('multiFlow', true);
    }
  }, [durationDays, multiFlow, form]);

  useEffect(() => {
    if (!multiFlow && durationDays > 1) {
      form.setValue('durationDays', 1);

      // Also update the days array to match the new duration
      const currentDays = form.getValues('days') || [];
      if (currentDays.length > 1) {
        // Keep only the first day
        form.setValue('days', currentDays.slice(0, 1), { shouldValidate: true });
      }
    }
  }, [multiFlow, durationDays, form]);

  // Prevent changing multiFlow to false if there are multiple flows
  useEffect(() => {
    const hasMultipleFlows = eventFlows && eventFlows.length > 1;
    if (!multiFlow && hasMultipleFlows) {
      form.setValue('multiFlow', true);
    }
  }, [multiFlow, eventFlows, form]);

  if (!event) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">{t('event.detail.settingsMenu.general.title')}</Typography>
      <Controller
        name="type"
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            select
            variant="standard"
            label={t('event.eventType')}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{ minWidth: 120, width: '40%' }}
          >
            {Object.entries(EVENT_TYPE).map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {t(`event.types.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        name="title"
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            required
            fullWidth
            type="text"
            variant="standard"
            label={t('event.eventTitle')}
            helperText={fieldState.error ? fieldState.error.message : null}
            error={!!fieldState.error}
          />
        )}
      />

      <Controller
        name="location"
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            fullWidth
            type="text"
            variant="standard"
            label={t('event.location')}
            placeholder={t('event.locationPlaceholder')}
            helperText={fieldState.error ? fieldState.error.message : null}
            error={!!fieldState.error}
          />
        )}
      />

      <Controller
        name="description"
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            fullWidth
            multiline
            minRows={1}
            variant="standard"
            label={t('event.description')}
            helperText={fieldState.error ? fieldState.error.message : null}
            error={!!fieldState.error}
          />
        )}
      />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Controller
          name="startDate"
          control={form.control}
          render={({ field: { value, onChange }, fieldState }) => (
            <DatePicker
              label={t('event.startDate')}
              value={value ? new Date(value) : null}
              onChange={(newValue) => {
                const startDate = newValue ? newValue.toISOString() : null;
                onChange(startDate);

                if (startDate) {
                  const duration = form.getValues('durationDays') || 1;
                  const endDate = new Date(startDate);
                  endDate.setDate(endDate.getDate() + duration);
                  form.setValue('endDate', endDate.toISOString(), { shouldValidate: true });

                  // Update days array with new dates
                  const days = form.getValues('days') || [];
                  const updatedDays = days.map((day: DayConfig, index: number) => ({
                    ...day,
                    date: new Date(new Date(startDate).setDate(new Date(startDate).getDate() + index)).toISOString(),
                  }));
                  form.setValue('days', updatedDays, { shouldValidate: true });
                }
              }}
              minDate={new Date()}
              slotProps={{
                textField: {
                  variant: 'standard',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                  sx: { width: 120 },
                },
              }}
            />
          )}
        />

        {showMultiFlowComponents && (
          <Controller
            name="durationDays"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                onChange={(e) => {
                  const newDuration = Number(e.target.value);
                  field.onChange(e);

                  // Get current days array
                  const currentDays = form.getValues('days') || [];

                  if (newDuration > currentDays.length) {
                    // Add new days
                    const newDays = Array(newDuration - currentDays.length).fill({
                      startOffsetMinutes: timeToMinuteOffset(START_TIME),
                      openOffsetMinutes: timeToMinuteOffset(OPEN_TIME),
                      endOffsetMinutes: timeToMinuteOffset(END_TIME),
                    });
                    form.setValue('days', [...currentDays, ...newDays]);
                  } else if (newDuration < currentDays.length) {
                    // Remove extra days
                    form.setValue('days', currentDays.slice(0, newDuration));
                  }
                }}
                select
                variant="standard"
                label={t('event.durationDays')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 120 }}
              >
                {Array.from({ length: MAX_EVENT_DAYS }, (_, i) => i + 1)
                  .filter((day) => !minimumDurationDays || day >= minimumDurationDays)
                  .map((day) => (
                    <MenuItem key={day} value={day}>
                      {day} {day === 1 ? t('common.day') : t('common.days')}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
        )}

        {showMultiFlowComponents && (
          <Controller
            name="multiFlow"
            control={form.control}
            render={({ field }) => {
              const hasMultipleFlows = eventFlows && eventFlows.length > 1;
              const hasMultipleDays = durationDays > 1;
              const isDisabled = hasMultipleFlows || hasMultipleDays;

              return (
                <Stack alignItems="center">
                  <Typography variant="caption" sx={{ fontSize: '0.875rem', display: 'block', marginBottom: 0.5 }}>
                    {t('event.multiFlow')}
                  </Typography>
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    size="small"
                    disabled={isDisabled}
                  />
                  {isDisabled && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem', mt: 0.5, textAlign: 'center' }}
                    >
                      {hasMultipleFlows && hasMultipleDays
                        ? t('event.multiFlowDisabled.multipleFlowsAndDays')
                        : hasMultipleFlows
                          ? t('event.multiFlowDisabled.multipleFlows')
                          : t('event.multiFlowDisabled.multipleDays')}
                    </Typography>
                  )}
                </Stack>
              );
            }}
          />
        )}
      </Box>
    </Box>
  );
}
